import { createGoogleGenerativeAI } from "@ai-sdk/google";
import { createOpenAICompatible } from "@ai-sdk/openai-compatible";
import { APICallError } from "@ai-sdk/provider";
import type { ModelWithRetries } from "@mastra/core/agent";
import { z } from "zod";
import { post, proxyFetch } from "./media/net.js";
import type { UsageRow } from "./agent.js";

export interface ReviewModelConfig {
  apiKey: string;
  model: string;
  baseUrl?: string;
  maxAttempts?: number;
}

export interface ReviewRoute {
  provider: "google-gemini" | "doubao";
  model: string;
  apiKey: string;
  baseUrl: string;
  maxAttempts: number;
}

export function reviewRoutes(config: ReviewModelConfig): ReviewRoute[] {
  const prefix = config.model.slice(0, config.model.lastIndexOf("/") + 1);
  const names = [...new Set([config.model, ...["gemini-3.8-flash", "gemini-3.7-flash", "gemini-3.6-flash"].map((m) => prefix + m)])];
  const base = (config.baseUrl ?? "https://generativelanguage.googleapis.com/v1beta").replace(/\/+$/, "");
  const routes: ReviewRoute[] = names.map((model) => ({
    provider: "google-gemini", model, apiKey: config.apiKey,
    baseUrl: /\/v1(beta)?$/.test(base) ? base : `${base}/v1beta`,
    maxAttempts: config.maxAttempts ?? 3,
  }));
  routes.push({ provider: "doubao", model: process.env.ARK_MODEL?.trim() || "ep-20260721150136-98dj9",
    apiKey: process.env.ARK_API_KEY?.trim() ?? "",
    baseUrl: "https://ark.cn-beijing.volces.com/api/v3", maxAttempts: 3 });
  return routes;
}

/** Mastra retries the current model call; successful tool calls are not replayed. */
export function agentModels(config: ReviewModelConfig, onFailure?: (route: ReviewRoute, error: unknown) => void,
  onUsage?: (usage: UsageRow) => void): ModelWithRetries[] {
  let calls = 0;
  return reviewRoutes(config).filter((route) => route.apiKey).map((route) => {
    const model = route.provider === "google-gemini"
      ? createGoogleGenerativeAI({ apiKey: route.apiKey, baseURL: route.baseUrl, fetch: proxyFetch })(route.model)
      : createOpenAICompatible({ name: "doubao", apiKey: route.apiKey, baseURL: route.baseUrl, fetch: proxyFetch })(route.model);
    const generate = model.doGenerate.bind(model);
    model.doGenerate = async (options) => {
      const callKey = `call:${++calls}`;
      try {
        const result = await generate(options);
        const u = result.usage;
        // Mastra step metadata may still identify the primary model after recovery.
        // Record exactly once at the adapter that actually received this response.
        onUsage?.({ call_key: callKey, provider: route.provider, model: route.model,
          input_tokens: u.inputTokens.total ?? 0, cached_input_tokens: u.inputTokens.cacheRead ?? 0,
          output_tokens: u.outputTokens.total ?? 0, reasoning_tokens: u.outputTokens.reasoning ?? 0 });
        if (route.provider === "google-gemini" && (["content-filter", "error"].includes(result.finishReason.unified)
          || ["PROHIBITED_CONTENT", "SAFETY", "RECITATION", "BLOCKLIST", "MALFORMED_FUNCTION_CALL"].includes(result.finishReason.raw ?? ""))) {
          throw new APICallError({ message: `Gemini finishReason=${result.finishReason.raw ?? result.finishReason.unified}`,
            url: `${route.baseUrl}/models/${route.model}:generateContent`, requestBodyValues: {}, isRetryable: false });
        }
        return result;
      } catch (error) { onFailure?.(route, error); throw error; }
    };
    return { id: `${route.provider}/${route.model}`, model, maxRetries: route.maxAttempts - 1 };
  });
}

const geminiResponse = z.object({
  candidates: z.array(z.object({
    finishReason: z.string().optional(),
    content: z.object({ parts: z.array(z.object({ text: z.string().optional(), thought: z.boolean().optional() })) }).optional(),
  })).optional(),
  promptFeedback: z.object({ blockReason: z.string().optional() }).optional(),
  usageMetadata: z.object({
    promptTokenCount: z.number().optional(), cachedContentTokenCount: z.number().optional(),
    candidatesTokenCount: z.number().optional(), thoughtsTokenCount: z.number().optional(),
  }).optional(),
});
const arkResponse = z.object({
  status: z.string().optional(),
  incomplete_details: z.object({ reason: z.string().optional() }).nullish(),
  output: z.array(z.object({ type: z.string(), content: z.array(z.object({ type: z.string(), text: z.string().optional() })).optional() })).optional(),
  usage: z.object({ input_tokens: z.number().optional(), output_tokens: z.number().optional(),
    input_tokens_details: z.object({ cached_tokens: z.number().optional() }).optional(),
    output_tokens_details: z.object({ reasoning_tokens: z.number().optional() }).optional(),
  }).optional(),
});

class ResponseFailure extends Error {
  constructor(message: string, readonly retryable: boolean) { super(message); }
}

export class ReviewModelError extends Error {
  constructor(failures: string[], readonly usage: UsageRow[]) {
    super(`All review model routes failed:\n${failures.join("\n")}`);
  }
}

function transientNetwork(error: unknown): boolean {
  return error instanceof Error && (error.name === "TimeoutError"
    || (error instanceof TypeError && error.message === "fetch failed")
    || (error.cause instanceof Error && "code" in error.cause
      && ["ECONNRESET", "ECONNREFUSED", "ETIMEDOUT", "EAI_AGAIN", "UND_ERR_SOCKET", "UND_ERR_HEADERS_TIMEOUT", "UND_ERR_BODY_TIMEOUT"].includes(String(error.cause.code))));
}

export interface JsonRequest<T> {
  config: ReviewModelConfig;
  prompt: string;
  system?: string;
  schema: z.ZodType<T>;
  timeoutMs: number;
  video?: { data: string; fps: number };
  callKey: string;
  onResponse?: (route: ReviewRoute, attempt: number, raw: string) => Promise<void>;
}

export async function generateReviewJson<T>(request: JsonRequest<T>): Promise<{ value: T; usage: UsageRow[]; route: ReviewRoute }> {
  const failures: string[] = [];
  const usage: UsageRow[] = [];
  const schema = z.toJSONSchema(request.schema);
  for (const route of reviewRoutes(request.config)) {
    if (!route.apiKey) { failures.push(`${route.provider}/${route.model}: ARK_API_KEY is not set`); continue; }
    const google = route.provider === "google-gemini";
    const body = google ? {
      contents: [{ role: "user", parts: [
        ...(request.video ? [{ inline_data: { mime_type: "video/mp4", data: request.video.data }, video_metadata: { fps: request.video.fps } }] : []),
        { text: request.prompt },
      ] }],
      ...(request.system ? { systemInstruction: { parts: [{ text: request.system }] } } : {}),
      generationConfig: { temperature: 0, responseMimeType: "application/json", responseJsonSchema: schema },
    } : {
      model: route.model,
      input: [{ role: "user", content: [
        ...(request.video ? [{ type: "input_video", video_url: `data:video/mp4;base64,${request.video.data}`, fps: request.video.fps }] : []),
        { type: "input_text", text: request.prompt },
      ] }],
      ...(request.system ? { instructions: request.system } : {}),
      text: { format: { type: "json_schema", name: "review", schema } },
    };
    for (let attempt = 1; attempt <= route.maxAttempts; attempt++) {
      let response: Response;
      let raw: string;
      try {
        response = await post(google ? `${route.baseUrl}/models/${route.model}:generateContent` : `${route.baseUrl}/responses`,
          body, request.timeoutMs, google ? { "x-goog-api-key": route.apiKey } : { Authorization: `Bearer ${route.apiKey}` });
        raw = await response.text();
      } catch (error) {
        if (!transientNetwork(error)) throw error;
        failures.push(`${route.provider}/${route.model} attempt ${attempt}: ${String(error)}`);
        if (attempt < route.maxAttempts) await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
        continue;
      }
      // Persistence failures must propagate, not trigger another paid request.
      await request.onResponse?.(route, attempt, raw);
      try {
        if (!response.ok) throw new ResponseFailure(`HTTP ${response.status}: ${raw.slice(0, 400)}`,
          [408, 429].includes(response.status) || response.status >= 500);
        const payload: unknown = JSON.parse(raw);
        let text: string;
        const row: UsageRow = { call_key: `${request.callKey}:${route.provider}:${route.model}:${attempt}`,
          provider: route.provider, model: route.model, input_tokens: 0, output_tokens: 0, cached_input_tokens: 0, reasoning_tokens: 0 };
        if (google) {
          const parsed = geminiResponse.parse(payload);
          const u = parsed.usageMetadata;
          Object.assign(row, { input_tokens: u?.promptTokenCount ?? 0, output_tokens: u?.candidatesTokenCount ?? 0,
            cached_input_tokens: u?.cachedContentTokenCount ?? 0, reasoning_tokens: u?.thoughtsTokenCount ?? 0 });
          usage.push(row);
          const candidate = parsed.candidates?.[0];
          const reason = parsed.promptFeedback?.blockReason ?? candidate?.finishReason;
          if (reason && reason !== "STOP") throw new ResponseFailure(`finishReason=${reason}`, false);
          text = candidate?.content?.parts.filter((p) => !p.thought).map((p) => p.text ?? "").join("") ?? "";
        } else {
          const parsed = arkResponse.parse(payload);
          const u = parsed.usage;
          Object.assign(row, { input_tokens: u?.input_tokens ?? 0, output_tokens: u?.output_tokens ?? 0,
            cached_input_tokens: u?.input_tokens_details?.cached_tokens ?? 0, reasoning_tokens: u?.output_tokens_details?.reasoning_tokens ?? 0 });
          usage.push(row);
          if (parsed.status && parsed.status !== "completed") throw new ResponseFailure(`status=${parsed.status}; ${parsed.incomplete_details?.reason ?? ""}`, false);
          text = parsed.output?.filter((o) => o.type === "message").flatMap((o) => o.content ?? [])
            .filter((c) => c.type === "output_text").map((c) => c.text ?? "").join("") ?? "";
        }
        return { value: request.schema.parse(JSON.parse(text)), usage, route };
      } catch (error) {
        if (!(error instanceof ResponseFailure || error instanceof SyntaxError || error instanceof z.ZodError)) throw error;
        failures.push(`${route.provider}/${route.model} attempt ${attempt}: ${error.message}`);
        if (!(error instanceof ResponseFailure) || !error.retryable) break;
        if (attempt < route.maxAttempts) await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
      }
    }
  }
  throw new ReviewModelError(failures, usage);
}
